import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import UserInfoActions from '../../actions/UserInfoActions';
import SessionActions from '../../actions/SessionActions';
import Dropdown from '../Dropdown';
import { getPath } from 'utils/Router';
import config from '../../config.js';
import { I18nContext } from 'utils/I18n/context';

import Locale from '../Locale';
import UserStore from "../../stores/UserStore";

const images = {
    user: require('img/icons/user.svg'),
    profile: require('img/icons/header-menu-user.svg'),
    admin: require('img/icons/header-menu-admin.svg'),
    advertiser: require('img/icons/header-menu-advertiser.svg'),
    exit: require('img/icons/header-menu-exit.svg'),
};

function User(props) {
    const { pathname: propPathname, balanceAmount, currencyBalance, name, timezone } = props;
    const { t } = useContext(I18nContext) || {};
    const [state, setState] = useState({
        balanceAmount,
        currency: currencyBalance,
        name,
        timezone,
        isAdmin: UserStore.isAdmin(),
        isAdvertiser: UserStore.isAdvertiser(),
        userId: UserStore.getID(),
        impersonating: UserStore.getImpersonating()
    });
    const pathnameRef = useRef(propPathname);
    const staticNames = t?.user || {};
    const staticNamesMenu = t?.userMenu || {};

    useEffect(() => {
        setState((prev) => ({
            ...prev,
            balanceAmount,
            currency: currencyBalance,
            name,
            timezone
        }));
    }, [balanceAmount, currencyBalance, name, timezone]);

    const loadInfo = useCallback(() => {
        const pathname = props.location && props.location.pathname;
        if (pathname && pathname !== pathnameRef.current) {
            UserInfoActions.loadInfo();
            pathnameRef.current = pathname;
        }
    }, [props.location]);

    useEffect(() => {
        loadInfo();
    });

    const onExit = useCallback(() => {
        SessionActions.deleteAccessToken();
    }, []);

    const onCloseDropdown = useCallback(() => {
        const body = document.querySelector('body');
        body && body.click();
    }, []);

    const onClickExit = useCallback(() => {
        onExit();
        onCloseDropdown();
        const base = (config.cpaUrl || '').replace(/\/$/, '') || window.location.origin;
        const next = (config.static && config.static.exit) || '/user/logout';
        window.location.href = base + next;
    }, [onExit, onCloseDropdown]);

    const renderButtonAdmin = useCallback(() => {
        if (state.impersonating) {
            return (
                <li>
                    <a
                        href={`${config.adminUrl}/admin/users/edit/${state.userId}?_switch_user=_exit`}
                        title={`${staticNames.toAdminPanel}`}
                        onClick={onExit}
                    >
                        <i className="icon">
                            <img src={images.admin} alt="{staticNames.toAdminPanel}"/>
                        </i>
                        {staticNames.toAdminPanel}
                    </a>
                </li>
            );
        }
        if (state.isAdmin) {
            return (
                <li>
                    <a
                        title={staticNames.adminPanel}
                        href={`${config.adminUrl}/admin`}
                        onClick={onCloseDropdown}
                    >
                        <i className="icon">
                            <img src={images.admin} alt="{staticNames.adminPanel}"/>
                        </i>
                        {staticNames.adminPanel}
                    </a>
                </li>
            );
        }
        return null;
    }, [state.impersonating, state.userId, state.isAdmin, staticNames, onExit, onCloseDropdown]);

    const renderButtonPublisher = useCallback(() => {
        if (state.impersonating) {
            return (
                <li>
                    <a
                        href={`${config.sideBarUrl}`}
                        title={`${staticNames.toPublisherPanel}`}
                        onClick={onExit}
                    >
                        <i className="icon">
                            <img src={images.advertiser} alt="{staticNames.toPublisherPanel}"/>
                        </i>
                        {staticNames.toPublisherPanel}
                    </a>
                </li>
            );
        }
        if (state.isAdvertiser) {
            return (
                <li>
                    <a
                        title={staticNames.publisherPanel}
                        href={`${config.sideBarUrl}`}
                        onClick={onCloseDropdown}
                    >
                        <i className="icon">
                            <img src={images.advertiser} alt="{staticNames.publisherPanel}"/>
                        </i>
                        {staticNames.publisherPanel}
                    </a>
                </li>
            );
        }
        return null;
    }, [state.impersonating, state.isAdvertiser, staticNames, onExit, onCloseDropdown]);

    return (
        <div className="top-header__user-wrapp">
            <Locale />
            <Dropdown
                mainClass={{'top-header__user': true}}
                isCloseDocument={true}
                header={(
                    <span className="user-header__wrapper">
                        <img className="user-logo" src={images.user} alt=""/>
                        <span className="user-name hidden-xs hidden-sm">{state.name}</span>
                    </span>
                )}
            >
                <ul className="dropdown-menu">
                    <li>
                        <Link
                            to={getPath('profile')}
                            onClick={() => onCloseDropdown()}
                        >
                            <i className="icon">
                            <img src={images.profile} alt="{staticNamesMenu.profile}"/>
                            </i>
                            {staticNamesMenu.profile}
                        </Link>
                    </li>
                    <li>
                        <button
                            className="top-header__user__bottom__logOut bold btn-empty"
                            onClick={onClickExit}
                        >
                            <i className="icon">
                                <img src={images.exit} alt="{staticNamesMenu.profile}"/>
                            </i>
                            {staticNamesMenu.exit}
                        </button>
                    </li>
                    {renderButtonAdmin()}
                    {renderButtonPublisher()}
                </ul>
            </Dropdown>
        </div>
    );
}

User.propTypes = {
    pathname: PropTypes.string
};

User.defaultProps = {
    pathname: window.location.pathname
};

export default User;
